import { useEffect } from "react";
import {
  Outlet,
  createRootRoute,
  useLocation,
  useMatchRoute,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { QueryProvider } from "@/lib/query-provider";
import Navbar from "@/components/Navbar";
import Notifications from "@/components/Notifications";
import { AfterHoursOverrideDialog } from "@/components/AfterHoursOverrideDialog";
import { KitchenCapacityOverrideDialog } from "@/components/KitchenCapacityOverrideDialog";
import { MoveConfirmDialog } from "@/components/MoveConfirmDialog";
import NotFound from "@/components/NotFound";
import { OccupiedOverrideDialog } from "@/components/OccupiedOverrideDialog";
import { PinOverrideDialog } from "@/components/PinOverrideDialog";
import { WalkInZoneConfirmDialog } from "@/components/WalkInZoneConfirmDialog";
import { RevertTablesDialog } from "@/components/RevertTablesDialog";
import { TableConstraintsOverrideDialog } from "@/components/TableConstraintsOverrideDialog";
import SimulationPanel from "@/components/SimulationPanel";
import EnvironmentBar from "@/components/EnvironmentBar";
import { FullPageSpinner } from "@/components/ui/spinner";
import { useAuthStore } from "@/store/auth-store";
import { useAppStore } from "@/store/store";
import { useSession } from "@/hooks/use-auth";
import { useZones } from "@/hooks/use-zones";
import { useTables } from "@/hooks/use-tables";
import { useGuests } from "@/hooks/use-guests";
import { useActiveReservations, useLiveTodayReservations } from "@/hooks/use-reservations";
import { useActiveLocationTimezone, useLocations } from "@/hooks/use-locations";
import { useRealtime } from "@/hooks/use-realtime";
import { todayStr } from "@/utils/date";

export const Route = createRootRoute({
  // No synchronous redirect to /login here — the session lives in an
  // HttpOnly cookie that JS cannot inspect, so the guard runs as a query
  // inside ``AuthenticatedShell``. ``isAuthenticated`` in the store is a
  // local hint only; the round-trip to ``/auth/me`` is the source of truth.
  component: RootLayout,
  notFoundComponent: NotFound,
});

function AuthenticatedShell() {
  const navigate = useNavigate();
  const session = useSession();
  // The waitlist-qr view is meant to live fullscreen on a tablet by the
  // door. Hide the side nav (and the mobile bottom nav) there so the QR
  // carries the whole stage.
  const pathname = useLocation({ select: (l) => l.pathname });
  const hideChrome = pathname.startsWith("/waitlist-qr");

  // Bounce to /login the moment the server confirms the session is gone.
  useEffect(() => {
    if (session.isError) navigate({ to: "/login" });
  }, [session.isError, navigate]);

  const { data: locations } = useLocations();
  const locationId = useAuthStore((s) => s.locationId);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // One sede → auto-select. Two+ → redirect to /select-location.
  useEffect(() => {
    if (!locations || locationId) return;
    if (locations.length === 1) {
      useAuthStore.getState().setLocation(locations[0].id);
    } else if (locations.length > 1) {
      navigate({ to: "/select-location" });
    }
  }, [locations, locationId, navigate]);

  // Logout flips ``isAuthenticated`` to false while this shell is still
  // mounted; drive the route change ourselves so the screen doesn't sit
  // blank until the next user-triggered navigation.
  useEffect(() => {
    if (!isAuthenticated && !session.isLoading) navigate({ to: "/login" });
  }, [isAuthenticated, session.isLoading, navigate]);

  // Location-scoped data (zones, tables, reservations) lives in the app
  // store and is refetched by the hooks below whenever locationId changes.
  // Wipe it synchronously on change so the map doesn't render the previous
  // sede's layout until the API responds.
  useEffect(() => {
    useAppStore.setState({ zones: [], tables: [], reservations: [] });
  }, [locationId]);

  // Realtime WebSocket updates — opens socket when authenticated.
  useRealtime();

  // Fetch all data from API → synced to store via hooks
  useZones();
  useTables();
  useGuests();

  // Align ``selectedDate`` with the location's timezone — "today" in a
  // restaurant context means the restaurant's local day, not the operator's
  // browser day. Without this, reservations whose local day differs from the
  // browser's (common when the staff browser or container sits in UTC) drop
  // out of the map filter. Only rewrite when the store still holds the
  // browser's "today" default, so an explicit date pick survives re-renders.
  const locationTz = useActiveLocationTimezone();
  useEffect(() => {
    if (!locationTz) return;
    const browserToday = todayStr();
    const localToday = todayStr(locationTz);
    if (browserToday === localToday) return;
    if (useAppStore.getState().selectedDate === browserToday) {
      useAppStore.setState({ selectedDate: localToday });
    }
  }, [locationTz]);

  // Two reservation queries feed the store, scoped to different days so they
  // don't clobber each other. Active drives the sidebar/lists for whichever
  // day the user is browsing; Live keeps today's rows in the store so the
  // floor map always reflects the real "now" even when the sidebar moves
  // away. Both share `useReservations`, so each one merges only its own day.
  useActiveReservations();
  useLiveTodayReservations();

  // Hold behind the splash until ``/auth/me`` resolves successfully.
  // ``isPending`` (rather than ``isLoading``) also catches the case where
  // a stale error cache from /login is still in flight: ``isError`` is
  // handled by the effect above which redirects to /login.
  if (session.isPending) return <FullPageSpinner />;

  if (!isAuthenticated) return null;

  // Hold the shell in a neutral loading state until both the location is
  // picked AND the locations list has resolved. Without the ``locations``
  // gate, a stale ``locationId`` hydrated from sessionStorage lets the map
  // render for a frame before the auto-select effect bounces the user to
  // /select-location.
  if (!locationId || !locations) return <FullPageSpinner />;

  return (
    <div className="flex flex-col md:flex-row h-[100dvh] overflow-hidden">
      {!hideChrome && <Navbar />}
      <main
        className="flex-1 min-w-0 min-h-0 overflow-hidden flex flex-col bg-surface-cream lg:bg-transparent"
        style={{ paddingBottom: hideChrome ? "0" : "var(--mobile-nav-h)" }}
      >
        <Outlet />
      </main>
      <KitchenCapacityOverrideDialog />
      <AfterHoursOverrideDialog />
      <PinOverrideDialog />
      <WalkInZoneConfirmDialog />
      <RevertTablesDialog />
      <TableConstraintsOverrideDialog />
      <OccupiedOverrideDialog />
      <MoveConfirmDialog />
      {import.meta.env.VITE_ALLOW_DEV_TOOLS === "true" && <SimulationPanel />}
      <Notifications />
    </div>
  );
}

function RootLayout() {
  const matchRoute = useMatchRoute();
  const isLoginPage = matchRoute({ to: "/login" });
  const isSelectLocationPage = matchRoute({ to: "/select-location" });
  // 404s render full-screen without going through ``AuthenticatedShell``;
  // otherwise an unauthenticated visitor would get bounced to /login before
  // ever seeing the not-found page.
  const isNotFound = useRouterState({
    select: (s) => s.matches.some((m) => m.status === "notFound"),
  });
  const bypassShell = isLoginPage || isSelectLocationPage || isNotFound;

  return (
    <QueryProvider>
      <EnvironmentBar />
      {bypassShell ? <Outlet /> : <AuthenticatedShell />}
    </QueryProvider>
  );
}
